import type { AppContext } from 'types/common'
import type { BaseAttachmentFile } from '@isoftdata/svelte-attachments'

import component from './SalesOrder.svelte'
import { graphql, type MessageTrigger$options, type MessageType$options, type SalesOrder$result } from '$houdini'
import { getSession } from 'stores/session'
import { v4 as uuid } from '@lukeed/uuid'
import { hasPermission } from 'utility/permission'

export type SalesOrderMessageData = {
	id: number
	text: string
	type: MessageType$options
	important: boolean
	trigger: MessageTrigger$options
}
export type SalesOrderCustomerMessage = SalesOrderMessageData[]
export type SalesOrderInventoryMessage = Record<
	number,
	{
		partNumber: string
		messages: SalesOrderMessageData[]
	}
>

export default function createState({ mediator, stateRouter, i18next: { t: translate } }: AppContext) {
	stateRouter.addState({
		name: 'app.sales-order',
		route: 'sales-order/:storeId/:salesOrderId',
		querystringParameters: ['salesOrderId', 'storeId', 'lastResetTime'],
		defaultParameters: {
			salesOrderId: null,
			storeId() {
				return getSession()?.currentStore?.toString() ?? null
			},
			lastResetTime: null,
		},
		canLeaveState(domApi) {
			// @ts-expect-error it's fine
			// eslint-disable-next-line @typescript-eslint/no-unsafe-call
			if (!domApi?.canLeaveState()) {
				return confirm(
					translate(
						'common:youHaveUnsavedChangesAreYouSureYouWantToLeave',
						'You have unsaved changes. Are you sure you want to leave?',
					),
				)
			}
			return true
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(_data, parameters) {
			const salesOrderId = parseInt(parameters.salesOrderId, 10) || null
			const storeId = parseInt(parameters.storeId, 10) || null

			const { data } = await initialLoadQuery.fetch({
				policy: 'CacheOrNetwork',
			})

			if (!data) {
				throw new Error('Failed to load page data')
			}

			let salesOrder: SalesOrder$result['salesOrder'] | null = null
			let attachments: Array<BaseAttachmentFile & { salesOrderFileId: number }> = []
			let salesOrderCustomerMessage: SalesOrderCustomerMessage | null = null
			let salesOrderInventoryMessage: SalesOrderInventoryMessage | null = null
			if (salesOrderId && storeId) {
				const { data } = await salesOrderQuery.fetch({
					variables: {
						key: {
							storeId,
							salesOrderId,
						},
						messageFilter: {
							triggers: ['SALES_ORDER', 'GENERAL'],
							display: ['BOTH', 'SHOW'],
							active: true,
						},
						pagination: {
							pageNumber: 1,
							pageSize: 0,
						},
					},
				})

				if (!data) {
					throw new Error('Failed to load sales order')
				}

				salesOrder = data.salesOrder
				attachments =
					data.salesOrder?.attachments.map(attachment => ({
						...attachment.file,
						fileId: attachment.file.id,
						salesOrderFileId: attachment.id,
						public: attachment.public,
						mimeType: attachment.file.mimetype,
						rank: attachment.rank ?? 0,
						createdDate: attachment.file.created.toISOString(),
						uuid: uuid(),
					})) ?? []

				salesOrderCustomerMessage =
					data.salesOrder?.customer && data.salesOrder.customer.messages.length > 0
						? data.salesOrder.customer.messages.filter(
								(message): message is Omit<typeof message, 'text'> & { text: string } => !!message.text,
							)
						: null

				salesOrderInventoryMessage =
					data.salesOrder?.lines.items.reduce((acc: SalesOrderInventoryMessage, line) => {
						if (line.inventory?.messages && line.inventory.messages.length > 0) {
							acc[line.inventory.inventoryId] = {
								// using line.lookup as partNumber to match what user sees on sales order lines
								partNumber: line.lookup,
								messages: line.inventory.messages.reduce((msgAcc: SalesOrderMessageData[], message) => {
									if (message.text) {
										msgAcc.push({
											id: message.id,
											text: message.text,
											type: message.type,
											important: message.important,
											trigger: message.trigger,
										})
									}
									return msgAcc
								}, []),
							}
						}
						if (line.inventory?.inventoryType?.messages && line.inventory.inventoryType.messages.length > 0) {
							if (!acc[line.inventory.inventoryId]) {
								acc[line.inventory.inventoryId] = {
									partNumber: line.lookup,
									messages: line.inventory.inventoryType.messages.reduce((msgAcc: SalesOrderMessageData[], message) => {
										if (message.text) {
											msgAcc.push({
												id: message.id,
												text: message.text,
												type: message.type,
												important: message.important,
												trigger: message.trigger,
											})
										}
										return msgAcc
									}, []),
								}
							} else {
								acc[line.inventory.inventoryId].messages.push(
									...line.inventory.inventoryType.messages.reduce((msgAcc: SalesOrderMessageData[], message) => {
										if (message.text) {
											msgAcc.push({
												id: message.id,
												text: message.text,
												type: message.type,
												important: message.important,
												trigger: message.trigger,
											})
										}
										return msgAcc
									}, []),
								)
							}
						}
						return acc
					}, {}) ?? null
			}

			return {
				salesOrder,
				salesOrderId,
				attachments,
				salesOrderCustomerMessage,
				salesOrderInventoryMessage,
				storeId: storeId ?? getSession()?.currentStore ?? null,
				salesPersonList: data.userAccounts,
				shippingMethods: data.shippingMethods,
				taxItems: data.taxItems,
				SODucumnetTypes: data.salesOrderDocumentTypes,
				salesOrderTerms: data.salesOrderTerms,
				SOSettingValues: data.settingValues.salesOrders,
				editEnabled: hasPermission('SALES_ORDERS_CAN_EDIT', storeId ?? undefined),
				canChangeDate: hasPermission('SALES_ORDERS_CAN_CHANGE_DATE', storeId ?? undefined),
			}
		},
	})
}

const initialLoadQuery = graphql(`
	query InitialLoadQuery($filter: UserAccountFilter) {
		shippingMethods {
			id
			name
		}
		userAccounts(filter: $filter) {
			id
			name
		}
		taxItems {
			id
			name
		}
		salesOrderDocumentTypes {
			id
			name
		}
		salesOrderTerms {
			id
			name
			chargeDays
			chargeDayOfMonth
		}
		settingValues {
			salesOrders {
				showSequentialInvoiceNumbers
			}
		}
	}
`)

const salesOrderQuery = graphql(`
	query SalesOrder($key: SalesOrderKey!, $messageFilter: MessageFilter, $pagination: PaginationOptions) {
		salesOrder(key: $key) {
			id
			adjustmentTotal
			allowBackorder
			allowPartialShipment
			appliedPaymentTotal
			balance
			creditMethod
			date
			dateEntered
			dateFinalized
			dateModified
			documentType {
				id
				name
				canExpire
			}
			expirationDate
			externalComments
			internalComments
			finalized
			paid
			purchaseOrderNumber
			salesOrderId
			sequentialId
			shippingTrackingNumber
			subtotal
			tax
			total
			truckNumber
			void
			lines(pagination: $pagination) {
				items {
					id
					lookup
					description
					cost
					hold
					price
					quantity
					taxable
					total
					type
					coreStatus
					backorderQuantity
					lostSaleQuantity
					quantityPicked
					quantityRequested
					quantityReturned
					manufacturer {
						id
						code
						name
					}
					inventory {
						id
						inventoryId
						quantity
						quantityAvailable
						quantityOnHold
						quantityOnOrder
						partNumber
						partNumberStripped
						taxable
						messages(filter: $messageFilter) {
							id
							text
							type
							trigger
							important
						}
						inventoryType {
							id
							messages(filter: $messageFilter) {
								id
								type
								text
								trigger
								important
							}
						}
						locations {
							location {
								name
							}
						}
						store {
							code
						}
					}
					locations {
						location {
							... on VirtualLocation {
								name
							}
							... on Location {
								name
							}
						}
					}
					vehicle {
						stockNumber
						taxable
					}
				}
			}
			attachments {
				id
				public
				rank
				file {
					id
					created
					extension
					mimetype
					name
					hash
					type
					updated
					path
					size
					host
					url
				}
			}
			adjustments {
				id
				adjustmentType {
					id
					name
					code
				}
				amount
				date
				description
				subtotalAdjustment
				taxable
				void
			}
			appliedPaymentLines {
				id
				amount
				payment {
					id
					comments
					date
					dateEntered
					documentNumber
					paymentMethod {
						name
					}
					store {
						name
					}
					storeId
					void
					createdBy {
						name
					}
				}
			}
			customer {
				id
				companyName
				balance
				accountLimit
				addresses {
					id
					label
				}
				messages(filter: $messageFilter) {
					id
					text
					type
					trigger
					important
				}
			}
			billingAddress {
				address1
				city
				country
				state
				zip
				company
				contact
				phone
			}
			shippingAddress {
				address1
				city
				company
				contact
				state
				zip
				country
				phone
			}
			shippingMethod {
				id
			}
			salesperson {
				id
				name
			}
			taxItem {
				id
				name
			}
			term {
				id
				name
				chargeDays
				chargeDayOfMonth
			}
		}
	}
`)
